import { Fragment, useEffect, useState } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertIcon,
  Button,
  chakra,
  Checkbox,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Controller, useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import InputField from '../../components/InputField'
import { RootState } from '../../app/store'
import { AustralianState, Patient, PatientWithOptionalId } from '../../types'
import { ATSI_STATUS_OPTIONS } from './constants'
import { useAppSelector } from '../../app/typedReduxHooks'
import AddressSuggestionInput from '../../components/form/AddressSuggestionInput'
import { trimValue } from '../../utils/text-formatter'
import { validateHumanName } from '../../utils/validation-helpers/Common'
import DateOfBirthSelectGroup from '../../components/DateOfBirthSelectGroup'
import {
  addressValidator,
  emailValidator,
  phoneNumberValidator,
  postcodeValidator,
} from '../../features/patient/utils/patientFieldsValidator'
import { PrescriberSelector } from '../../components/PrescriberSelector'
import { LimitedPrescriberProfile } from '../../features/users/UsersSlice'
import { Permission } from '../../types/permission'
import IHIBuilder from './components/IHIBuilder'
import {
  expandYearMonthMedicareValidToEndOfDay,
  medicareValidToToMonthInputValue,
} from '../../utils/patient/patientPayload'

interface PatientFormProps {
  defaultPatient?: Patient | PatientWithOptionalId
  selectedPrescriber: LimitedPrescriberProfile | null
  onPrescriberChange?: (id: string) => void
  hidePrescriberSelection?: boolean
  isSharedPatientsFeatureOn: boolean
  isDisabled?: boolean
}

const AddressSuggestion = chakra(AddressSuggestionInput)

function PatientForm(props: PatientFormProps) {
  const {
    defaultPatient,
    selectedPrescriber,
    onPrescriberChange,
    hidePrescriberSelection = false,
    isSharedPatientsFeatureOn,
    isDisabled = false,
  } = props
  const {
    register,
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext()
  const [isExpanded, setIsExpanded] = useState(false)
  const [showAddressLine2, setShowAddressLine2] = useState(!!getValues().address_2)
  const [isShowHospitalProviderNumber, setIsShowHospitalProviderNumber] = useState(false)
  const [isShowAddressSuggestion, setIsShowAddressSuggestion] = useState(true)

  const prescriberState = useSelector((state: RootState) => state.prescriber)
  const hasPatientManagement = useAppSelector((state) =>
    state.authentication.permissions?.includes(Permission.PATIENT_MANAGEMENT)
  )
  const currentUser = useAppSelector((state) => state.authentication)
  const users = useAppSelector((state) => state.users?.data)
  const activePrescriberUsers = users?.filter((user) => user?.prescriber?.active) ?? []

  const effectivePrescriber = hidePrescriberSelection
    ? selectedPrescriber || prescriberState.prescriber
    : selectedPrescriber

  const hasValidHpii = !!effectivePrescriber?.hpii

  useEffect(() => {
    if (
      defaultPatient?.address_1 &&
      defaultPatient?.suburb &&
      defaultPatient?.postcode &&
      defaultPatient?.state
    ) {
      setIsShowAddressSuggestion(false)
    }
  }, [
    defaultPatient?.address_1,
    defaultPatient?.suburb,
    defaultPatient?.postcode,
    defaultPatient?.state,
  ])

  useEffect(() => {
    setIsShowHospitalProviderNumber(!!defaultPatient?.hospital_category)
  }, [defaultPatient?.hospital_category])

  useEffect(() => {
    if (hasPatientManagement && currentUser?.userId && !selectedPrescriber?.id) {
      const currentUserData = users?.find((u) => u.userId === currentUser.userId)
      if (currentUserData?.prescriber?.active) {
        onPrescriberChange?.(currentUserData.prescriber.id)
      }
    }
  }, [hasPatientManagement, currentUser?.userId, users, selectedPrescriber, onPrescriberChange])

  return (
    <VStack alignItems="stretch" paddingY={[1, 4]} paddingX={[1, 10]} spacing={12}>
      {!hidePrescriberSelection && hasPatientManagement && !defaultPatient && (
        <VStack alignItems="stretch" flex={1} spacing={4}>
          <Heading as="h4" size="md">
            Prescriber Selection
          </Heading>
          <SimpleGrid columns={[1, 2]} spacingX={8} spacingY={4}>
            <InputField label="Select Prescriber" field="prescriber" isRequired={true}>
              <PrescriberSelector
                users={activePrescriberUsers || []}
                selectedPrescriberId={selectedPrescriber?.id}
                onSelect={(id) => id && onPrescriberChange?.(id)}
              />
            </InputField>
          </SimpleGrid>
        </VStack>
      )}
      {!hasPatientManagement || hidePrescriberSelection || selectedPrescriber ? (
        !hasValidHpii && !defaultPatient?.ihi_number ? (
          <Alert status="warning">
            <AlertIcon />
            {hidePrescriberSelection ? (
              <Text>You don't have a valid HPII number.</Text>
            ) : (
              <Text>
                Selected prescriber does not have a valid HPII number. Please select a different
                prescriber.
              </Text>
            )}
          </Alert>
        ) : (
          <Fragment>
            <VStack
              as="fieldset"
              disabled={isDisabled}
              alignItems="stretch"
              flex={1}
              spacing={4}
              border={0}
              minWidth={0}
              p={0}
              m={0}
            >
              <Heading as="h4" size="md">
                Contact Information
              </Heading>
              <SimpleGrid columns={[1, 2]} spacingX={8} spacingY={4}>
                <InputField
                  label="First Names"
                  field="given_names"
                  errors={errors}
                  isRequired={true}
                >
                  <Input
                    {...register('given_names', {
                      setValueAs: trimValue,
                      required: 'Required',
                      maxLength: {
                        value: 40,
                        message: 'Patient name must be less than 40 Characters',
                      },
                      validate: (value?: string) => {
                        if (!value || value.trim() === '') {
                          return 'First name cannot be empty'
                        }
                        const result = validateHumanName(value)
                        return result.isValid || result.displayValue
                      },
                    })}
                  ></Input>
                </InputField>
                <InputField
                  label="Family Name"
                  field="family_name"
                  errors={errors}
                  isRequired={true}
                >
                  <Input
                    {...register('family_name', {
                      setValueAs: trimValue,
                      required: 'Required',
                      maxLength: {
                        value: 40,
                        message: 'Patient name must be less than 40 Characters',
                      },
                      validate: (value?: string) => {
                        if (!value || value.trim() === '') {
                          return 'Family name cannot be empty'
                        }
                        const result = validateHumanName(value)
                        return result.isValid || result.displayValue
                      },
                    })}
                  />
                </InputField>
                <InputField label="Gender" field="gender" isRequired={true} errors={errors}>
                  <Select
                    {...register('gender', {
                      required: 'Required',
                      validate: (value?: string) => {
                        if (!value || value.trim() === '') {
                          return 'Gender cannot be empty'
                        }
                        return true
                      },
                    })}
                  >
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="I">Intersex or Indeterminate</option>
                    <option value="N">Not Stated</option>
                  </Select>
                </InputField>
                <DateOfBirthSelectGroup
                  register={register}
                  setValue={setValue}
                  errors={errors}
                  defaultDateOfBirth={defaultPatient?.date_of_birth}
                />
                <InputField
                  label="Mobile Number"
                  field="phone_number"
                  isRequired={true}
                  errors={errors}
                >
                  <Input
                    placeholder="eg. 0411222333"
                    {...register('phone_number', {
                      setValueAs: trimValue,
                      required: false,
                      validate: (value: string) => {
                        if (value.length > 0 && value.trim() === '') {
                          return 'Mobile number cannot be empty if provided'
                        }
                        return phoneNumberValidator(value, getValues().email)
                      },
                      deps: ['email'],
                    })}
                  ></Input>
                </InputField>
                <InputField label="Email" field="email" isRequired={true} errors={errors}>
                  <Input
                    {...register('email', {
                      setValueAs: trimValue,
                      required: false,
                      validate: (email: string) => {
                        if (email.length > 0 && email.trim() === '') {
                          return 'Email cannot be empty if provided'
                        }
                        return emailValidator(email, getValues().phone_number)
                      },
                      deps: ['phone_number'],
                    })}
                  ></Input>
                </InputField>
              </SimpleGrid>

              <FormControl
                isInvalid={!!(errors.address_1 || errors.suburb || errors.postcode || errors.state)}
              >
                <AddressSuggestion
                  setValue={setValue}
                  gridColumn={['1 / 2', '1 / span 2']}
                  setIsShowAddressSuggestion={setIsShowAddressSuggestion}
                  {...(!isShowAddressSuggestion && { display: 'none' })}
                />
                {isShowAddressSuggestion && (
                  <FormErrorMessage>Please fill in all the address related fields</FormErrorMessage>
                )}
              </FormControl>
              <VStack
                alignItems="stretch"
                display={isShowAddressSuggestion ? 'none' : 'flex'}
                gap={4}
              >
                <Flex justifyContent="space-between" mt={4}>
                  <Text fontWeight="bold">Manual Address Entry</Text>
                  <Button
                    variant="link"
                    color="blue.500"
                    onClick={() => setIsShowAddressSuggestion(true)}
                    size="sm"
                  >
                    Back to address search
                  </Button>
                </Flex>
                <InputField
                  label="Residential Address"
                  field="address_1"
                  isRequired={true}
                  errors={errors}
                >
                  <Input
                    {...register('address_1', {
                      setValueAs: trimValue,
                      required: 'Required',
                      validate: (value?: string) => {
                        if (!value || value.trim() === '') {
                          return 'Address cannot be empty'
                        }
                        return addressValidator(value)
                      },
                    })}
                  ></Input>
                </InputField>
                {!showAddressLine2 && (
                  <Button onClick={() => setShowAddressLine2(true)} color="grey">
                    + Add address line 2
                  </Button>
                )}
                {showAddressLine2 && (
                  <InputField label="Residential Address Line 2" field="address_2" errors={errors}>
                    <Input
                      {...register('address_2', {
                        setValueAs: trimValue,
                        validate: addressValidator,
                      })}
                    ></Input>
                  </InputField>
                )}
                <SimpleGrid columns={[1, 3]} spacingX={8} spacingY={4}>
                  <InputField label="Suburb" field="suburb" isRequired={true} errors={errors}>
                    <Input
                      {...register('suburb', {
                        setValueAs: trimValue,
                        required: 'Required',
                        maxLength: 36,
                        validate: (value?: string) => {
                          if (!value || value.trim() === '') {
                            return 'Suburb cannot be empty'
                          }
                          // suburb and human name have same validation rule
                          const result = validateHumanName(value)
                          return result.isValid || result.displayValue
                        },
                      })}
                    ></Input>
                  </InputField>
                  <InputField label="Postcode" field="postcode" isRequired={true} errors={errors}>
                    <Input
                      type="text"
                      {...register('postcode', {
                        setValueAs: trimValue,
                        required: 'Required',
                        validate: (value?: string | number) => {
                          if (!value || (typeof value === 'string' && value.trim() === '')) {
                            return 'Postcode cannot be empty'
                          }
                          return postcodeValidator(`${value}`)
                        },
                      })}
                    ></Input>
                  </InputField>
                  <InputField label="State" field="state" isRequired={true} errors={errors}>
                    <Select
                      {...register('state', {
                        required: 'Required',
                        validate: (input?: string) => {
                          if (!input || input.trim() === '') {
                            return 'State cannot be empty'
                          }
                          return (
                            Object.values(AustralianState).includes(input as AustralianState) ||
                            'Please select a valid State'
                          )
                        },
                      })}
                    >
                      <>
                        {Object.values(AustralianState).map((state) => {
                          return (
                            <option value={state} key={state}>
                              {state}
                            </option>
                          )
                        })}
                      </>
                    </Select>
                  </InputField>
                </SimpleGrid>
              </VStack>
            </VStack>
            <IHIBuilder {...props} effectivePrescriber={effectivePrescriber} />

            <VStack
              as="fieldset"
              disabled={isDisabled}
              flexGrow="1"
              alignItems="stretch"
              border={0}
              minWidth={0}
              p={0}
              m={0}
            >
              <Heading as="h4" size="md">
                Prescription Type
              </Heading>
              <Text fontSize="sm">Choose the default prescription method for this patient</Text>
              <Checkbox flexGrow="1" {...register('paper_only')}>
                Paper Prescriptions Only
              </Checkbox>
            </VStack>
            <HStack spacing={8} paddingY={10}>
              <Heading as="h4" size="md">
                Additional Information
              </Heading>
              <Button
                colorScheme="teal"
                variant="link"
                textDecoration={'underline'}
                color={'blue.400'}
                onClick={() => setIsExpanded(!isExpanded)}
                minW="auto"
                px={1}
                leftIcon={
                  isExpanded ? <ChevronUpIcon boxSize={8} /> : <ChevronDownIcon boxSize={8} />
                }
              >
                {isExpanded ? 'Collapse' : 'Expand'}
              </Button>
            </HStack>
            {isExpanded && (
              <chakra.fieldset disabled={isDisabled} border={0} minWidth={0} p={0} m={0}>
                <VStack alignItems="stretch" flex={1} spacing={4}>
                  <Heading as="h4" size="md">
                    Additional Entitlement Details (Optional)
                  </Heading>
                  <Stack alignItems="start" spacing={[4, 8]} direction={['column', 'row']}>
                    <VStack alignItems="stretch" flex={1} spacing={4}>
                      <InputField label="RACFID (Aged Care Patient)" field="racfid" errors={errors}>
                        <Input
                          type="text"
                          {...register('racfid', { setValueAs: trimValue })}
                        ></Input>
                      </InputField>
                      <InputField
                        label="Pension or Health Care Card Number"
                        field="concession_pension_no"
                        errors={errors}
                      >
                        <Input
                          type="text"
                          {...register('concession_pension_no', {
                            setValueAs: trimValue,
                          })}
                        ></Input>
                      </InputField>
                      <InputField label="Entitlement Number" field="entitlement_no" errors={errors}>
                        <Input
                          type="text"
                          {...register('entitlement_no', { setValueAs: trimValue })}
                        ></Input>
                      </InputField>
                    </VStack>
                    <VStack alignItems="stretch" flex={1} spacing={3}>
                      <InputField label="" field="ctg_flag" errors={errors}>
                        <Checkbox {...register('ctg_flag')}>Eligible for Closing the Gap</Checkbox>
                      </InputField>
                      <InputField label="" field="hospital_category" errors={errors}>
                        <Checkbox
                          {...register('hospital_category')}
                          onChange={(e) => setIsShowHospitalProviderNumber(e.target.checked)}
                        >
                          Hospital Inpatient
                        </Checkbox>
                      </InputField>
                      {isShowHospitalProviderNumber && (
                        <InputField
                          label="Hospital Provider Number"
                          field="hospital_provider_number"
                          errors={errors}
                        >
                          <Input
                            type="text"
                            {...register('hospital_provider_number', {
                              setValueAs: trimValue,
                              validate: (hospitalProviderNumber: any) => {
                                const isHospitalCategoryChecked = getValues().hospital_category
                                if (isHospitalCategoryChecked && !hospitalProviderNumber) {
                                  return 'Please fill the Hospital Provider Number when you check the Hospital Inpatient'
                                }
                                return true
                              },
                              deps: ['hospital_category'],
                            })}
                          ></Input>
                        </InputField>
                      )}
                      <InputField label="ATSI Status" field="atsi_status" errors={errors}>
                        <Select {...register('atsi_status')}>
                          <>
                            <option value={undefined} key={'undefined_atsi_status'}>
                              {}
                            </option>
                            {Object.entries(ATSI_STATUS_OPTIONS).map(([value, lable]) => {
                              return (
                                <option value={value} key={value}>
                                  {lable}
                                </option>
                              )
                            })}
                          </>
                        </Select>
                      </InputField>
                    </VStack>
                  </Stack>
                </VStack>
                <VStack alignItems="stretch" flex={1} spacing={4} mt={8}>
                  <Heading as="h4" size="md">
                    Additional Medicare Details (Optional)
                  </Heading>
                  <Container
                    width={['100%', '50%']}
                    paddingLeft={0}
                    paddingRight={4}
                    marginLeft={0}
                    marginRight="auto"
                  >
                    <InputField label="Medicare Expiry" field="medicare_valid_to" errors={errors}>
                      <Controller
                        name="medicare_valid_to"
                        control={control}
                        rules={{
                          validate: (medicareValidTo: string | undefined) => {
                            if (
                              !isExpanded ||
                              (!medicareValidTo?.trim() &&
                                !getValues().medicare_no &&
                                !getValues().medicare_irn)
                            ) {
                              return true
                            }
                            const message =
                              'Please enter a valid date that medicare valid to, eg: 2027-07'
                            const valid = medicareValidTo?.search(/^\d{4}-\d{2}/) !== -1
                            return valid || message
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            type="month"
                            value={medicareValidToToMonthInputValue(field.value)}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            onChange={(e) => {
                              const ym = e.target.value
                              if (!ym) {
                                field.onChange('')
                                return
                              }
                              const expanded = expandYearMonthMedicareValidToEndOfDay(ym)
                              field.onChange(expanded ?? '')
                            }}
                          />
                        )}
                      />
                    </InputField>
                  </Container>
                </VStack>
              </chakra.fieldset>
            )}
          </Fragment>
        )
      ) : (
        <Alert status="info">
          <AlertIcon />
          <Text>
            {isSharedPatientsFeatureOn
              ? 'No valid prescriber from this organization'
              : 'Please select a prescriber before creating a patient'}
          </Text>
        </Alert>
      )}
    </VStack>
  )
}
export default PatientForm
